import dayjs, { Dayjs } from 'dayjs';
import { ApolloError } from '@apollo/client';

import { describeRateLimitRejection, getRateLimitRejection } from '../../../common/rateLimit';
import { CallLogRow, CallerCount, CrmCallFilter } from '../callLog/callLog';
import { DEFAULT_PAGE_SIZE, PAGE_SIZES, PageSize } from '../contacts/contactList';
import { CRM_PATHS } from '../crmPaths';

/**
 * The Follow-ups screen (card 22) as plain data and pure functions, tested
 * without rendering: its state and URL, the `crmCalls` filter it makes, and
 * the words for each follow-up.
 *
 * A follow-up is **open** from the call that set it until the person's next
 * call, or until an admin closes it; **due** once open and its date has come
 * (ilbp-backend docs/CARD_16_CALL_LOGGING.md §4). This screen lists the open
 * ones, due first: docs/CARD_22_FOLLOW_UPS.md.
 */

/* ------------------------------------------------------------------ *
 * What the API answers
 * ------------------------------------------------------------------ */

/** The call that set the follow-up, with what the screen needs of its person. */
export interface FollowUpRow extends Omit<CallLogRow, 'contact'> {
  contact: {
    id: number;
    fullName: string | null;
    primaryEmail: string | null;
    primaryPhone: string | null;
    doNotCall: boolean;
    doNotContact: boolean;
    canCall: boolean;
    mergedIntoId: number | null;
    phones: Array<{ id: number; e164: string | null; isPrimary: boolean; isBad: boolean }>;
  } | null;
}

/* ------------------------------------------------------------------ *
 * The screen's state, all of it in the URL
 * ------------------------------------------------------------------ */

/** Due: the date has come. Coming up: open, for a later date. */
export type FollowUpView = 'due' | 'upcoming';

export interface FollowUpsState {
  view: FollowUpView;
  /** Who set them: '' everyone, 'me', or 'a<id>' one admin. */
  by: string;
  page: number;
  pageSize: PageSize;
}

export const EMPTY_FOLLOW_UPS_STATE: FollowUpsState = { view: 'due', by: '', page: 0, pageSize: DEFAULT_PAGE_SIZE };

export const VIEWS: Array<{ id: FollowUpView; label: string; hint: string }> = [
  { id: 'due', label: 'Due', hint: 'The date has come, and nobody has called back or closed it.' },
  { id: 'upcoming', label: 'Coming up', hint: 'Open, for a later date.' },
];

/** The state a URL holds. Anything unreadable is left out, never guessed. */
export const parseFollowUpsState = (params: URLSearchParams): FollowUpsState => {
  const by = params.get('by') ?? '';
  const page = Number(params.get('page'));
  const size = Number(params.get('size'));
  return {
    view: params.get('view') === 'upcoming' ? 'upcoming' : 'due',
    by: /^(me|a[1-9]\d{0,9})$/.test(by) ? by : '',
    page: Number.isInteger(page) && page > 0 ? page : 0,
    pageSize: (PAGE_SIZES as ReadonlyArray<number>).includes(size) ? (size as PageSize) : DEFAULT_PAGE_SIZE,
  };
};

/** The URL for a state: only what differs from the empty one, so the due list is a plain link. */
export const toFollowUpsParams = (state: FollowUpsState): URLSearchParams => {
  const params = new URLSearchParams();
  if (state.view !== 'due') params.set('view', state.view);
  if (state.by) params.set('by', state.by);
  if (state.page) params.set('page', String(state.page));
  if (state.pageSize !== DEFAULT_PAGE_SIZE) params.set('size', String(state.pageSize));
  return params;
};

/** A change to what is shown starts again at the first page. */
export const withFollowUpsChange = (state: FollowUpsState, change: Partial<FollowUpsState>): FollowUpsState => ({
  ...state,
  ...change,
  page: 'page' in change ? (change.page as number) : 0,
});

/* ------------------------------------------------------------------ *
 * The crmCalls filter
 * ------------------------------------------------------------------ */

/** Who set them, as crmCalls' filter takes it. */
const byFilter = (by: string): CrmCallFilter =>
  by === 'me' ? { mine: true } : by.startsWith('a') ? { adminIds: [Number(by.slice(1))] } : {};

/**
 * One view's filter. `now` is fixed by the screen while the URL stays the
 * same, so the variables (and Apollo's cache) stay put between renders.
 * Ordered by follow-up date (`FOLLOW_UP`), the earliest first.
 */
export const buildFollowUpsFilter = (view: FollowUpView, by: string, now: Dayjs): CrmCallFilter => ({
  ...byFilter(by),
  followUp: 'OPEN',
  followUpAt: view === 'due' ? { to: now.toISOString() } : { from: now.toISOString() },
});

/** The call queue serving these follow-ups: every admin's, or the ones this admin set. */
export const queuePathForFollowUps = (by: string) => `${CRM_PATHS.callQueue}?mode=${by === 'me' ? 'mine' : 'followups'}`;

/* ------------------------------------------------------------------ *
 * Words
 * ------------------------------------------------------------------ */

const fullName = (person: { firstName: string | null; lastName: string | null } | null | undefined) =>
  person ? [person.firstName, person.lastName].filter(Boolean).join(' ') : '';

/** Who set it: "You", the admin's name, or their id when the name is not known. */
export const setByName = (row: Pick<FollowUpRow, 'adminId' | 'admin'>, myAdminId: number | null) => {
  if (row.adminId != null && row.adminId === myAdminId) return 'You';
  return fullName(row.admin) || (row.adminId != null ? `Admin #${row.adminId}` : '');
};

/** The admins in the "Set by" menu, from the counts: only admins set follow-ups. */
export const setByChoices = (callers: ReadonlyArray<CallerCount> | undefined) =>
  (callers ?? [])
    .filter((caller) => caller.adminId != null && !caller.isYou)
    .map((caller) => ({ key: `a${caller.adminId}`, name: fullName(caller.admin) || `Admin #${caller.adminId}`, count: caller.count }));

export const personName = (contact: FollowUpRow['contact'], contactId: number) =>
  contact?.fullName || contact?.primaryEmail || `Contact #${contactId}`;

/**
 * How far the date is, in whole local days: "Due today", "1 day overdue",
 * "12 days overdue"; ahead, "Today", "Tomorrow", "In 3 days".
 */
export const describeWhenDue = (followUpAt: string, now: Dayjs) => {
  const days = dayjs(followUpAt).startOf('day').diff(now.startOf('day'), 'day');
  if (days < 0) return `${-days} ${days === -1 ? 'day' : 'days'} overdue`;
  if (dayjs(followUpAt).isAfter(now)) return days === 0 ? 'Later today' : days === 1 ? 'Tomorrow' : `In ${days} days`;
  return 'Due today';
};

/** "15 Oct, 09:00", the year too when it is not this one. */
export const formatFollowUpAt = (value: string, now: Dayjs) =>
  dayjs(value).format(dayjs(value).isSame(now, 'year') ? 'ddd D MMM, HH:mm' : 'D MMM YYYY, HH:mm');

export type NotCallable = 'merged' | 'doNotContact' | 'doNotCall' | 'noNumber' | 'badNumber';

/**
 * Why the person cannot be rung now, or null when they can. In the API's
 * order (canCall): a merged-away record first, since nobody calls those.
 */
export const notCallable = (contact: FollowUpRow['contact']): NotCallable | null => {
  if (!contact) return null;
  if (contact.mergedIntoId != null) return 'merged';
  if (contact.doNotContact) return 'doNotContact';
  if (contact.doNotCall) return 'doNotCall';
  if (!contact.primaryPhone) return 'noNumber';
  if (!contact.canCall) return 'badNumber';
  return null;
};

export const NOT_CALLABLE: Record<NotCallable, { label: string; hint: string }> = {
  merged: {
    label: 'Merged',
    hint: 'This record was merged into another contact. A call logged on that contact closes this follow-up.',
  },
  doNotContact: { label: 'Do not contact', hint: 'Marked do not contact. Close the follow-up, or log a call they made to us.' },
  doNotCall: { label: 'Do not call', hint: 'Marked do not call. Close the follow-up, or log a call they made to us.' },
  noNumber: { label: 'No number', hint: 'No phone number on record. Add one on their page, or close the follow-up.' },
  badNumber: { label: 'Number marked bad', hint: 'Their main number is marked bad. Correct it on their page, or close the follow-up.' },
};

export const describeFollowUpCount = (count: number, view: FollowUpView) =>
  `${count.toLocaleString('en-US')} ${count === 1 ? 'follow-up' : 'follow-ups'} ${view === 'due' ? 'due' : 'coming up'}`;

/** What a refused page means. */
export const describeFollowUpsError = (error: ApolloError): string => {
  const rejection = getRateLimitRejection(error);
  if (rejection) return describeRateLimitRejection(rejection);
  const graphQL = error.graphQLErrors?.[0];
  if (graphQL?.extensions?.code === 'CRM_INVALID_QUERY') return graphQL.message;
  return 'The follow-ups could not be loaded. Try again in a moment.';
};
